import * as React from 'react';
import { useIntl } from "react-intl";
import { styled } from "stitches.config";
import { palette } from "theme/palette";

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Typography from "../sushi/Typography";
import { TextShadowH1 } from "../TextShadow/TextShadow";
import { SeoTextWithNavigation, SeoTextWithNavigationEntry } from "../Seo/SeoTextWithNavigation";

export type SimulatorLayoutProps = {
    title: string;
    subtitle: string;
    seoEntries: SeoTextWithNavigationEntry[];
    // Override the default disclaimer block. In-scope simulators (Compound, Simple)
    // pass <ComplianceDisclaimer />. Out-of-scope simulators (Monthly Payments) leave
    // it undefined and fall back to the original informational sentence.
    disclaimer?: React.ReactNode;
}

export const SimulatorLayout = ({ children, subtitle, title, seoEntries, disclaimer }: React.PropsWithChildren<SimulatorLayoutProps>) => {
    const { formatMessage } = useIntl();
    return (
      <Wrapper id="home-wrapper">
        <Header />
        <Hero>
          <Title>
            <Typography variant="headingM" as={TextShadowH1} center>
              {title}
            </Typography>
            <Subtitle>
              <Typography variant="bodyM" color="secondary" center font="montreal">
                {subtitle}
              </Typography>
            </Subtitle>
          </Title>
          <SimulatorContainer>
            {children}
            <DisclaimerContainer>
              {disclaimer ?? (
                <Typography variant="subHeadingM" color="secondaryAlt">
                  {formatMessage({ id: 'monthly_payments_simulator.disclaimer' })}
                </Typography>
              )}
            </DisclaimerContainer>
          </SimulatorContainer>
        </Hero>
        <ContentContainer>
          <Content>
            <SeoTextWithNavigation entries={seoEntries} />
          </Content>
        </ContentContainer>
        <div>
          <Footer bannerTitle="app.footer.bannerTitle" />
        </div>
      </Wrapper>
    )
}


const Wrapper = styled('div', {
    overflow: 'hidden auto',
  });
  
  const Hero = styled('div', {
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 72.5%, #000 100%), radial-gradient(31.28% 31.28% at 50% 50%, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0.00) 100%), radial-gradient(72.85% 52.12% at 0% 0%, rgba(0, 0, 0, 0.64) 0%, rgba(0, 0, 0, 0.00) 100%), radial-gradient(89.59% 64.1% at 79.31% 100%, rgba(241, 192, 134, 0.16) 0%, rgba(241, 192, 134, 0.00) 100%), radial-gradient(84.88% 60.73% at 25.42% 24.43%, rgba(30, 30, 89, 0.40) 0%, rgba(30, 30, 89, 0.00) 100%), radial-gradient(46.2% 33.06% at 22.6% 56.08%, rgba(241, 192, 134, 0.12) 0%, rgba(241, 192, 134, 0.00) 100%)',
    textAlign: 'center',
    paddingTop: '160px',
    paddingBottom: '80px',
    display: 'flex',
    flexDirection: 'column',
    gap: '80px',
    alignItems: 'center',
    '@sm': {
      paddingTop: '128px',
      gap: '$12',
      paddingBottom: '$8',
    }
  });
  
  const Title = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '$3',
    paddingLeft: '$5',
    paddingRight: '$5',
    '@sm': {
      gap: '$5',
    },
  });

  const Subtitle = styled('div', {
    maxWidth: '680px',
  });
  
  
  const SimulatorContainer = styled('div', {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '$6'
  });

  const DisclaimerContainer = styled('div', {
    width: '100%',
    maxWidth: 1040,
    paddingLeft: '$5',
    paddingRight: '$5',
  });
  
  const ContentContainer = styled('div', {
    width: '100%',
    paddingTop: '80px',
    background: palette.white,
    '@sm': {
      paddingTop: '$10',
    }
  });
  
  const Content = styled('div', {
    width: '100%',
    maxWidth: 1264,
    margin: '0 auto',
    padding: '$6',
  });
  